import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { Alert, Loading } from '../../components';
import { useEntitlements } from '../../contexts/Entitlements';

import './catalog-feeds-panel.scss';

/**
 * Primary Admin → Catalog Feeds (Feature 5).
 *
 * A store admin sets up their own WhatsApp/Meta and Google Shopping product
 * feeds without RC involvement: copy the URL, follow the steps, paste it into
 * Meta Commerce Manager / Google Merchant Center.
 *
 * The URL exists on every plan — a store can finish its Meta/Google setup on
 * Free and the feed fills the moment the feature is added. When the plan
 * doesn't include it we say so plainly (the feed serves empty).
 */

const STEPS = {
  whatsapp: {
    where: 'Meta Commerce Manager',
    link: 'https://business.facebook.com/commerce',
    steps: [
      'Open Meta Commerce Manager and pick (or create) the catalogue linked to your WhatsApp Business account.',
      'Go to Catalogue → Data sources → Add items → Scheduled feed.',
      'Paste the feed URL below when asked for the data feed URL. No username or password is needed.',
      'Set the update schedule to Daily (hourly if Meta offers it) and finish.',
      'Meta fetches the feed and your listings appear in the WhatsApp catalogue after its first successful import.',
    ],
    note: 'Your listings appear in the WhatsApp catalogue only after Meta approves the items — first import can take a few hours.',
  },
  google: {
    where: 'Google Merchant Center',
    link: 'https://merchants.google.com',
    steps: [
      'Open Google Merchant Center and select your account.',
      'Go to Products → Feeds → Add primary feed.',
      'Choose your country and language, then select Scheduled fetch as the input method.',
      'Paste the feed URL below as the file URL and set a daily fetch schedule.',
      'Save. Google fetches the feed and your products appear once they pass review.',
    ],
    note: 'Google reviews products before they show in Shopping — allow up to 3 working days for the first fetch.',
  },
};

function CopyField({ value }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <div className="cf-url">
      <input type="text" readOnly value={value} onFocus={(e) => e.target.select()} />
      <button type="button" className="rc-btn rc-btn--small" onClick={copy}>
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );
}

function FeedCard({ feed, gatingActive }) {
  const [open, setOpen] = useState(false);
  const guide = STEPS[feed.key] || { steps: [], where: '', link: null };
  const locked = gatingActive && !feed.included;

  return (
    <div className={`cf-card ${locked ? 'cf-card--locked' : ''}`}>
      <div className="cf-card__head">
        <span className="cf-card__title">{feed.label}</span>
        <span className={`cf-card__state ${feed.included ? 'cf-card__state--on' : ''}`}>
          {feed.included ? 'Included in your plan' : 'Not in your plan'}
        </span>
      </div>

      {locked && (
        <p className="cf-card__locked-note">
          This feed is not included in your current plan, so it currently serves an empty
          catalogue. You can still set it up now — it fills automatically once the feature
          is added to your plan.
        </p>
      )}

      <CopyField value={feed.url} />

      <button type="button" className="cf-card__toggle" onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide setup steps' : `How to connect this to ${guide.where}`}
      </button>

      {open && (
        <div className="cf-steps">
          <ol>
            {guide.steps.map((step) => <li key={step}>{step}</li>)}
          </ol>
          {guide.note && <p className="cf-steps__note">{guide.note}</p>}
          {guide.link && (
            <a href={guide.link} target="_blank" rel="noreferrer noopener" className="cf-steps__link">
              Open {guide.where} ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function CatalogFeedsPanel() {
  const { gatingActive } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/store/catalog-feeds');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load your catalog feeds.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm(
      'Generate new feed URLs? The current URLs stop working immediately — you must paste the new ones into Meta and Google, or those catalogues will stop updating.'
    )) return;

    setRotating(true);
    setError('');
    try {
      const res = await axios.post('/api/store/catalog-feeds/rotate');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not generate new URLs.');
    } finally {
      setRotating(false);
    }
  };

  if (loading) return <Loading />;
  if (error && !data) return <Alert type="error" message={error} />;
  if (!data) return null;

  return (
    <div className="cf-panel">
      <p className="cf-panel__intro">
        Publish your live stock to WhatsApp and Google automatically. Copy a feed URL,
        paste it into the platform once, and it updates itself from then on — listings
        you add, reprice or sell follow through on the platform&apos;s next fetch.
      </p>

      {data.feeds.map((feed) => (
        <FeedCard key={feed.key} feed={feed} gatingActive={gatingActive} />
      ))}

      {!data.is_legacy && (
        <div className="cf-panel__footer">
          <span>
            Treat these URLs as passwords — anyone with one can read your live catalogue.
          </span>
          <button type="button" className="rc-btn rc-btn--small" disabled={rotating} onClick={rotate}>
            {rotating ? 'Generating…' : 'Generate new URLs'}
          </button>
        </div>
      )}

      {error && <Alert type="error" message={error} />}
    </div>
  );
}

export default CatalogFeedsPanel;
